import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { ShieldAlert, Eye, EyeOff, KeyRound, LogOut } from 'lucide-react';

/**
 * Blocking overlay that forces the user to change their initial password.
 * Renders when user.must_change_password is true.
 * Mounted at the App root so it covers the whole UI regardless of route.
 */
export default function ForcePasswordChange() {
  const { logout, clearMustChangePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const meetsLength = next.length >= 8;
  const meetsMix = /[a-zA-Z]/.test(next) && /\d/.test(next);
  const matches = next && next === confirm;
  const valid = meetsLength && meetsMix && matches && current.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error('Revisa los requisitos antes de continuar');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Contraseña actualizada. ¡Bienvenido/a!');
      clearMustChangePassword();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar la contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      data-testid="force-password-change"
    >
      <Card className="w-full max-w-md shadow-2xl border-amber-200">
        <CardHeader className="text-center space-y-2">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-7 h-7 text-amber-600" />
          </div>
          <CardTitle className="text-xl">Cambia tu contraseña</CardTitle>
          <p className="text-sm text-slate-500">
            Por seguridad, debes establecer una nueva contraseña antes de usar la aplicación por primera vez.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs">Contraseña actual (la que recibiste por email)</Label>
              <div className="relative">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={current}
                  onChange={e => setCurrent(e.target.value)}
                  placeholder="••••••••"
                  required autoFocus
                  className="pr-10"
                  data-testid="fpc-current"
                />
                <button type="button" onClick={() => setShowCurrent(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Nueva contraseña</Label>
              <div className="relative">
                <Input
                  type={showNext ? 'text' : 'password'}
                  value={next}
                  onChange={e => setNext(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  required minLength={8}
                  className="pr-10"
                  data-testid="fpc-new"
                />
                <button type="button" onClick={() => setShowNext(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Confirmar nueva contraseña</Label>
              <Input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                data-testid="fpc-confirm"
              />
            </div>

            {/* Password strength checklist */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-xs">
              <Req met={meetsLength} text="Al menos 8 caracteres" />
              <Req met={meetsMix} text="Combina letras y números" />
              <Req met={matches} text="Las dos contraseñas coinciden" />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={logout}
                className="flex-1" data-testid="fpc-logout">
                <LogOut className="w-4 h-4 mr-1" /> Cerrar sesión
              </Button>
              <Button type="submit" disabled={!valid || submitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="fpc-submit">
                <KeyRound className="w-4 h-4 mr-1" />
                {submitting ? 'Guardando...' : 'Cambiar contraseña'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

const Req = ({ met, text }) => (
  <div className={`flex items-center gap-2 ${met ? 'text-emerald-600' : 'text-slate-500'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${met ? 'bg-emerald-500' : 'bg-slate-300'}`} />
    {text}
  </div>
);
